import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Flame, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import OAuthButtons from "@/components/OAuthButtons";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const startGoogleAuth = () => {
    const redirectUrl = window.location.origin + "/auth/callback";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

export default function Login() {
    const { login } = useAuth();
    const navigate = useNavigate();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [submitting, setSubmitting] = useState(false);

    const onSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            await login(email, password);
            toast.success("Welcome back.");
            navigate("/app");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Login failed");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen bg-[#0a0a0a] text-white flex items-center justify-center px-6 relative overflow-hidden">
            <div className="absolute inset-0 orange-glow pointer-events-none" />
            <div className="w-full max-w-md relative">
                <Link to="/" className="flex items-center gap-2 mb-10" data-testid="login-logo">
                    <Flame className="w-5 h-5 text-signal" />
                    <span className="font-heading font-black text-xl tracking-tight">
                        sales<span className="text-signal">whisper</span>
                    </span>
                </Link>
                <h1 className="font-heading font-bold text-4xl mb-2">Sign in</h1>
                <p className="text-zinc-400 mb-8 text-sm">Welcome back, closer.</p>

                <OAuthButtons onGoogle={startGoogleAuth} testidPrefix="login-oauth" />

                <div className="flex items-center gap-4 my-6">
                    <div className="flex-1 h-px bg-zinc-900" />
                    <span className="text-xs font-mono uppercase tracking-widest text-zinc-600">or sign in with email</span>
                    <div className="flex-1 h-px bg-zinc-900" />
                </div>

                <form onSubmit={onSubmit} className="space-y-4" data-testid="login-form">
                    <div>
                        <label className="block text-xs font-mono uppercase tracking-widest text-zinc-500 mb-2">Email</label>
                        <input
                            data-testid="login-email-input"
                            type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                            className="w-full bg-zinc-900 border border-zinc-800 rounded-md text-white px-4 py-3 focus:outline-none focus:ring-1 focus:ring-signal focus:border-signal"
                            placeholder="you@domain.com"
                            autoFocus
                        />
                    </div>
                    <div>
                        <label className="block text-xs font-mono uppercase tracking-widest text-zinc-500 mb-2">Password</label>
                        <input
                            data-testid="login-password-input"
                            type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                            className="w-full bg-zinc-900 border border-zinc-800 rounded-md text-white px-4 py-3 focus:outline-none focus:ring-1 focus:ring-signal focus:border-signal"
                            placeholder="••••••••"
                        />
                    </div>
                    <button
                        data-testid="login-submit-btn"
                        type="submit" disabled={submitting}
                        className="w-full bg-signal hover:bg-signal-hover text-white px-6 py-3.5 rounded-md font-semibold flex items-center justify-center gap-2 transition-colors disabled:opacity-60"
                    >
                        {submitting ? "Signing in..." : <>Sign in <ArrowRight className="w-4 h-4" /></>}
                    </button>
                </form>

                <p className="text-sm text-zinc-500 mt-8">
                    New here?{" "}
                    <Link to="/signup" className="text-signal hover:underline" data-testid="login-signup-link">Create an account</Link>
                </p>
            </div>
        </div>
    );
}
